import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { domAnimation, LazyMotion } from "framer-motion";
import { PlatformProvider } from "./PlatformProvider";
import { ErrorBoundary } from "./ErrorBoundary";
import { Toaster } from "@/components/ui/Feedback";
import { HomeScreen } from "@/features/course/HomeScreen";
import { BackendModulesScreen } from "@/features/course/BackendModulesScreen";
import { BackendLessonsScreen } from "@/features/course/BackendLessonsScreen";
import { BackendLessonScreen } from "@/features/course/BackendLessonScreen";
import { AssignmentChatScreen } from "@/features/ai/AssignmentChatScreen";
import { AiToolsScreen } from "@/features/ai/AiToolsScreen";
import { BackendProfilesScreen } from "@/features/profile/BackendProfilesScreen";
import { ProfileScreen } from "@/features/profile/ProfileScreen";
import { ProfileSurveyScreen } from "@/features/profile/SurveyScreen";
import { HelpScreen } from "@/features/service/HelpScreen";
import { NotFoundScreen } from "@/features/service/NotFoundScreen";
import { OfflineBanner } from "@/features/service/OfflineBanner";
import { AuthGate } from "@/features/service/AuthGate";

/**
 * Маршруты приложения.
 *
 * Все экраны работают на данных бэкенда: демонстрационных экранов и мок-API
 * в приложении нет. Вход и регистрация живут внутри AuthGate, поэтому здесь
 * только то, что доступно после успешного входа.
 */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />

      <Route path="/home" element={<HomeScreen />} />

      <Route path="/course" element={<BackendModulesScreen />} />
      <Route
        path="/course/modules/:moduleId"
        element={<BackendLessonsScreen />}
      />
      <Route
        path="/course/lessons/:assignmentId"
        element={<BackendLessonScreen />}
      />

      {/* Инструменты ассистента — те же задания, только короткий путь к ним */}
      <Route path="/assistant" element={<AiToolsScreen />} />

      {/* Задание выполняется диалогом с ассистентом */}
      <Route
        path="/homework/:assignmentId/chat"
        element={<AssignmentChatScreen />}
      />

      <Route path="/profile" element={<ProfileScreen />} />
      <Route path="/profiles" element={<BackendProfilesScreen />} />
      <Route path="/profile/survey" element={<ProfileSurveyScreen />} />

      <Route path="/help" element={<HelpScreen />} />
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
}

export function App() {
  return (
    <PlatformProvider>
      {/*
        Анимации подключаются облегчённым набором: полный компонент `motion`
        тянет в бандл все возможности библиотеки, а приложению нужны только
        появление и сдвиг. `strict` запрещает `motion.*` — с ним экономия не
        отменится случайной правкой.
      */}
      <LazyMotion features={domAnimation} strict>
        {/*
        Роутер по путям, не по hash: Telegram и MAX передают данные запуска в
        hash-фрагменте, поэтому HashRouter ломает каждый запуск из мессенджера.
        Регрессия закрыта тестом в App.test.tsx.
      */}
        <BrowserRouter>
          <OfflineBanner />
          {/*
          Граница ошибок внутри роутера: без неё исключение в любом экране
          оставляет в мессенджере белый экран без выхода
        */}
          <ErrorBoundary>
            <AuthGate>
              <AppRoutes />
            </AuthGate>
          </ErrorBoundary>
          <Toaster />
        </BrowserRouter>
      </LazyMotion>
    </PlatformProvider>
  );
}
